import { useState } from 'react';

import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';

import { feedbackApiRef } from '../../api';

export const FeedbackDialog = () => {
  const theme = useTheme();

  const useStyles = makeStyles(() => ({
    verticalButton: {
      position: 'fixed',
      top: '50%',
      right: '14px',
      transform: 'translateY(-50%) rotate(-90deg)',
      transformOrigin: 'right center',
      backgroundColor: theme.palette.stylingD.background,
      color: theme.palette.stylingD.text,
      padding: '8px 16px',
      borderRadius: '4px 4px 0 0',
      zIndex: 1400,
      cursor: 'pointer',
      boxShadow: theme.shadows[4],
    },
    scoreButton: {
      minWidth: 36,
      minHeight: 36,
      fontWeight: 'bold',
      borderRadius: 4,
      border: '1px solid transparent',
      fontSize: '0.85rem',
    },
    snackbarBox: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.stylingD.text,
      padding: theme.spacing(1.5, 3),
      borderRadius: theme.shape.borderRadius,
    },
  }));

  const classes = useStyles();

  const location = useLocation();
  const identity = useApi(identityApiRef);
  const feedbackApi = useApi(feedbackApiRef);

  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState('');

  const handleDialogOpen = () => {
    setOpen(true);
    setRating(null);
    setComment('');
    setError('');
  };

  const handleDialogClose = () => {
    setOpen(false);
    setRating(null);
    setComment('');
    setError('');
  };

  const handleSubmit = async () => {
    if (rating === null) {
      setError('Please select a rating before submitting.');
      return;
    }

    const user = await identity.getBackstageIdentity();

    const feedback = {
      rating,
      comment,
      location: window.location.href,
      userRef: user.userEntityRef || 'anonymous',
      createdAt: new Date().toISOString(),
    };

    await feedbackApi.submitFeedback(feedback);
    handleDialogClose();
    setSnackbarOpen(true);
  };

  const getButtonColor = (val: number) => {
    if (val <= 6) return theme.palette.stylingC;
    if (val <= 8) return theme.palette.stylingB;
    return theme.palette.stylingA;
  };

  const shouldShowButton = !location.pathname.startsWith('/feedback');

  const getEmoji = (val: number) => {
    if (val <= 6) return '😞 Unhappy';
    if (val <= 8) return '🙂 Okay';
    return '😍 Happy';
  };

  return (
    <>
      {shouldShowButton && (
        <button className={classes.verticalButton} onClick={handleDialogOpen}>
          Send Feedback
        </button>
      )}
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Rate Your Experience</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            <strong>How likely are you to recommend this? (1–10)</strong>
          </Typography>
          <Box display="flex" justifyContent="flex-start" flexWrap="wrap">
            {Array.from({ length: 10 }).map((_, i) => {
              const val = i + 1;
              const { background, text } = getButtonColor(val);
              return (
                <Tooltip key={val} title={getEmoji(val)} arrow>
                  <Button
                    className={classes.scoreButton}
                    style={{
                      marginLeft: val === 1 ? 0 : 4,
                      marginTop: 8,
                      backgroundColor: background,
                      color: text,
                    }}
                    onClick={() => {
                      setRating(val);
                      setError('');
                    }}
                    variant={rating === val ? 'contained' : 'outlined'}
                  >
                    {val}
                  </Button>
                </Tooltip>
              );
            })}
          </Box>

          {error && (
            <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
              {error}
            </Typography>
          )}

          <TextField
            label="Comments (optional)"
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            margin="normal"
            placeholder="Let us know what went well or what could be improved!"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={rating === null}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={classes.snackbarBox}>
          <Typography variant="body2">
            Feedback submitted successfully!
          </Typography>
        </Box>
      </Snackbar>
    </>
  );
};
